/**
 * ONE Sentry scope shape, for two Sentry SDKs.
 *
 * `src/data/telemetry.js` (browser, `@sentry/react`) and
 * `src/reports/server/telemetry.js` (Node, `@sentry/node`) had the same
 * twenty-line `withScope` body character for character, because they must: the
 * whole point of the field vocabulary is that ONE Sentry query spans the browser
 * lane and the Function lane of the same request. Two copies of a convention is
 * how the convention stops being one — the browser's `org.id` and the server's
 * `orgId` would look identical in review and answer different queries.
 *
 * So the body lives here, once, and takes the SDK as an argument. The module
 * imports nothing, which is what lets the browser bind the React SDK at module
 * scope while the Function binds `@sentry/node` lazily, on the first fault, once
 * it has initialised it.
 *
 * WHAT THE SHAPE IS, AND WHY (from `src/data/telemetry.js`'s header):
 *
 *   1. FIELD NAMES. `component` / `action` / `org.id` on every event, so one
 *      query spans the whole layer.
 *   2. TAGS vs CONTEXT. Sentry INDEXES tags and does not index context. "Which
 *      orgs saw this?" is a tag question and is unanswerable if the org id went
 *      into `extra`. The split is decided here, once.
 *   3. TELEMETRY MUST NEVER THROW. A failure inside observability that takes
 *      down a save is strictly worse than the blind spot it was closing.
 *
 * Levels are `info`, `warning`, `error`. NOT `debug` — audit-logging check 9
 * rules it out, and a debug breadcrumb is invisible anyway unless some later
 * capture happens to carry it.
 */

/**
 * Sentry silently drops a tag whose value is not a short scalar, and a dropped
 * tag is worse than a wrong one because nothing says it went missing.
 */
export const tagValue = (value) => String(value).slice(0, 200);

/**
 * Bind the scope shape to one Sentry SDK.
 *
 * @param {object|null} sentry            `@sentry/react` or `@sentry/node`, or
 *   null — a null SDK makes the returned function a no-op, which is how the
 *   Function behaves wherever no `SENTRY_DSN` is configured.
 * @param {{defaultComponent?: string, unknownFault?: string}} [opts]
 *   `unknownFault` is the message used when a caller passes no fault at all; it
 *   is a CONSTANT per lane so those events group into one issue instead of one
 *   issue per call site.
 * @returns {(fault: Error|string, ctx?: object) => void}
 */
export function createCapture(
  sentry,
  { defaultComponent = "data", unknownFault = "unknown data-layer fault" } = {},
) {
  return function capture(fault, { component, action, orgId, level = "error", tags, context } = {}) {
    try {
      if (!sentry?.withScope) return;
      sentry.withScope((scope) => {
        scope.setLevel(level);
        scope.setTag("component", component ?? defaultComponent);
        if (action) scope.setTag("action", action);
        // "none" rather than omitting it: an event with no org is a real and
        // distinguishable case (a call made before an org was opened), and an
        // absent tag cannot be searched for.
        scope.setTag("org.id", orgId ? tagValue(orgId) : "none");

        for (const [key, value] of Object.entries(tags ?? {})) {
          if (value !== undefined && value !== null) scope.setTag(key, tagValue(value));
        }
        // One card named "data" on every event, so the shape is the same
        // everywhere and a reader does not have to hunt for the payload.
        if (context) scope.setContext("data", context);

        // A real Error keeps its stack, so Sentry groups by where it actually
        // came from rather than by this file. A string is used only where there
        // is no error object, and those messages are deliberately constant so
        // they group into one issue.
        if (fault instanceof Error) sentry.captureException(fault);
        else sentry.captureMessage(String(fault ?? unknownFault), level);
      });
    } catch {
      // Observability must never break the path it observes.
    }
  };
}

export default createCapture;
